import React from 'react';
import { Dialog, Button, Flex, Text, Textarea, NumberInput, DatePicker, useToasts } from '@applyboard/crystal-ui';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useManualPaymentActivity } from './useManualPaymentActivity';

const maxDate = new Date().toISOString();
const minDate = new Date(2000, 0, 1).toISOString();

export function ManualPaymentDialog() {
  const toasts = useToasts();
  const activity = useManualPaymentActivity();
  const { apiError } = useErrorHandler();

  const [description, setDescription] = React.useState('');
  const [amountReceived, setAmountReceived] = React.useState('');
  const [dateReceived, setDateReceived] = React.useState(new Date().toISOString());

  const isValid = description.trim().length > 0 && parseFloat(amountReceived) > 0 && dateReceived.length > 0;

  const onSubmit = () => {
    activity
      .submitManualPayment({
        description,
        amountReceived: parseFloat(amountReceived),
        dateReceived: dateReceived.split('T')[0],
      })
      .then(() => {
        toasts.positive('Payment recorded successfully.');
        activity.completeProcess();
      })
      .catch(() => apiError('Failed to record payment. Please try again.'));
  };

  return (
    <Dialog
      size="md"
      open={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) activity.completeProcess();
      }}
    >
      <Dialog.Heading>Record Manual Payment</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={6}>
          <Text variant="bodyM" intent="secondary">
            Record a payment received outside the system. This will update the application status to Paid.
          </Text>
          <Textarea
            label="Payment Description"
            value={description}
            onChange={(value: string) => setDescription(value)}
            required
          />
          <Flex direction="row" gap={4}>
            <Flex grow={1}>
              <NumberInput
                label="Amount Received"
                value={amountReceived}
                onChange={(value) => setAmountReceived(value)}
                placeholder="0.00"
                required
              />
            </Flex>
            <Flex grow={1}>
              <DatePicker
                label="Date Received"
                value={dateReceived}
                onChange={(value) => setDateReceived(value)}
                maxDate={maxDate}
                minDate={minDate}
                granularity="day"
              />
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton disabled={activity.isBusy} width="fill">
          Cancel
        </Dialog.CloseButton>
        <Button intent="primary" onClick={onSubmit} width="fill" disabled={!isValid} loading={activity.isBusy}>
          Confirm Payment
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
